/**
 * ShiftQuickAddModal — small focused dialog opened by clicking an empty slot
 * on the Rota Calendar. Pre-fills locked context (the SU or worker whose
 * calendar we are on, the date, the start time, and a default +1 h end). The
 * user only needs to fill in the *other* side of the pairing (worker for the
 * SU pivot, SU for the worker pivot) and click Save.
 *
 * On success it returns the created shift to the parent via `onCreated` so
 * the parent can splice it into local state and render the block immediately
 * — no full reload, no scroll.
 */

import { useEffect, useState } from "react";
import { Loader2, X, Save, AlertTriangle, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/dates";

const SHIFT_TYPES = ["Regular Visit", "Sleep-In", "Double-Up", "Cover Visit", "Training", "Standby", "Office"];

function timeToMinutes(t) {
  const [h, m] = (t || "00:00").split(":").map(Number);
  return h * 60 + m;
}

function durationLabel(start, end, overnight) {
  let mins = timeToMinutes(end) - timeToMinutes(start);
  if (overnight || mins <= 0) mins += 24 * 60;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function nextDateIso(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

export default function ShiftQuickAddModal({
  pivot,            // "service_user" | "worker"
  lockedEntity,     // { id, label, sub } — the SU or worker whose calendar we're on
  lockedDate,       // YYYY-MM-DD
  initialStart,     // HH:MM
  initialEnd,       // HH:MM (defaults to +1h on the parent side)
  staff = [],       // list of staff (filtered to assignable roles below)
  users = [],       // list of service users
  existing = null,  // existing shift object → switches to EDIT mode (PATCH + Delete)
  onClose,
  onCreated,
  onUpdated,
  onDeleted,
}) {
  const isEdit = !!existing;
  const [start, setStart] = useState(existing?.start_time || initialStart);
  const [end, setEnd] = useState(existing?.end_time || initialEnd);
  const [overnight, setOvernight] = useState(!!existing?.overnight);
  const [shiftType, setShiftType] = useState(existing?.shift_type || "Regular Visit");
  // For edit mode the "other" side is pre-filled from the existing shift.
  const _initOther = existing
    ? (pivot === "service_user" ? existing.worker_id : (existing.visit_slots?.[0]?.service_user_id || ""))
    : "";
  const [otherId, setOtherId] = useState(_initOther);
  const [conflicts, setConflicts] = useState([]);
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [error, setError] = useState("");

  // Auto-tick overnight when the user picks Sleep-In, untick on revert.
  useEffect(() => {
    if (shiftType === "Sleep-In") setOvernight(true);
  }, [shiftType]);

  // For SU-pivot the user picks a worker, for worker-pivot they pick an SU.
  const otherList =
    pivot === "service_user"
      ? staff
          .filter((s) => ["care_worker", "bank_worker", "team_leader", "registered_manager", "deputy_manager"].includes(s.role))
          .filter((s) => (s.status || "active") === "active")
          .sort((a, b) => (a.full_name || "").localeCompare(b.full_name || ""))
      : users.slice().sort((a, b) => (a.full_name || "").localeCompare(b.full_name || ""));

  const workerId = pivot === "service_user" ? otherId : lockedEntity.id;
  const suId = pivot === "service_user" ? lockedEntity.id : otherId;

  // Live conflict check
  useEffect(() => {
    if (!workerId) { setConflicts([]); return; }
    let cancelled = false;
    api
      .post("/shifts/check-conflicts", { worker_id: workerId, date: lockedDate, start_time: start, end_time: end, overnight, exclude_shift_id: existing?.id })
      .then(({ data }) => { if (!cancelled) setConflicts(data.conflicts || []); })
      .catch(() => { if (!cancelled) setConflicts([]); });
    return () => { cancelled = true; };
  }, [workerId, lockedDate, start, end, overnight, existing?.id]);

  const blocking = conflicts.filter((c) => ["high", "critical"].includes(c.severity));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!workerId) { setError("Please pick a worker."); return; }
    if (!suId) { setError("Please pick a service user."); return; }
    if (blocking.length && (!override || !overrideReason.trim())) {
      setError("Conflicts detected — tick override and add a reason to proceed.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        worker_id: workerId,
        date: lockedDate,
        start_time: start,
        end_time: end,
        overnight,
        shift_type: shiftType,
        status: "published",
        visit_slots: [{ service_user_id: suId, planned_start: start, planned_end: end }],
        override_conflicts: override,
        override_reason: overrideReason,
      };
      if (isEdit) {
        const { data } = await api.patch(`/shifts/${existing.id}`, payload);
        onUpdated?.(data.shift || data);
      } else {
        const { data } = await api.post("/shifts", payload);
        onCreated?.(data.shift || data);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail?.message || "Failed to save shift.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!existing) return;
    const isConfirmed = existing.confirmation_status === "confirmed";
    let reason = "";
    if (isConfirmed) {
      reason = (window.prompt(
        `This shift on ${fmtDate(lockedDate)} (${existing.start_time}–${existing.end_time}) has already been ACCEPTED by the worker.\n\nEnter a reason for deletion (min 5 chars, audit-logged and sent to the worker):`,
        "",
      ) || "").trim();
      if (!reason) return;
      if (reason.length < 5) {
        setError("Reason must be at least 5 characters.");
        return;
      }
    } else {
      if (!window.confirm(`Delete this shift on ${fmtDate(lockedDate)} (${existing.start_time}–${existing.end_time})? This cannot be undone.`)) return;
    }
    setDeleteBusy(true);
    setError("");
    try {
      const qs = reason ? `?reason=${encodeURIComponent(reason)}` : "";
      await api.delete(`/shifts/${existing.id}${qs}`);
      onDeleted?.(existing.id);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail?.message || "Failed to delete shift.");
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="shift-quick-add-backdrop" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="shift-quick-add-modal"
      >
        <div className="flex items-start justify-between px-5 py-3 border-b border-slate-100">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{isEdit ? "Edit shift" : "Add shift"}</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {pivot === "service_user" ? "Service user" : "Worker"}: <strong className="text-slate-800">{lockedEntity.label}</strong>
              {lockedEntity.sub && <span className="text-slate-400"> · {lockedEntity.sub}</span>}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md hover:bg-slate-100" data-testid="shift-quick-add-close">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Locked context: date */}
          <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-700">
            <strong>{fmtDate(lockedDate)}</strong>
            {overnight && <span> → {fmtDate(nextDateIso(lockedDate))} <span className="text-emerald-700">(overnight)</span></span>}
            <span className="ml-2 text-slate-500">· {durationLabel(start, end, overnight)}</span>
          </div>

          {/* The other side of the pairing — the only field the RM has to pick. */}
          <label className="block">
            <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">{pivot === "service_user" ? "Worker" : "Service user"} *</span>
            <select
              required
              value={otherId}
              onChange={(e) => setOtherId(e.target.value)}
              data-testid="shift-quick-add-other-select"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
              autoFocus
            >
              <option value="">{pivot === "service_user" ? "Pick a worker…" : "Pick a service user…"}</option>
              {otherList.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.full_name}
                  {pivot === "service_user" && o.role ? ` (${o.role.replace(/_/g, " ")})` : ""}
                </option>
              ))}
            </select>
          </label>

          {/* Times — pre-filled, editable. */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">Start</span>
              <input type="time" required value={start} onChange={(e) => setStart(e.target.value)} data-testid="shift-quick-add-start" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">End</span>
              <input type="time" required value={end} onChange={(e) => setEnd(e.target.value)} data-testid="shift-quick-add-end" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
          </div>

          {/* Shift type + overnight */}
          <div className="grid grid-cols-2 gap-3 items-end">
            <label className="block">
              <span className="text-xs uppercase tracking-wide font-semibold text-slate-600">Visit type</span>
              <select value={shiftType} onChange={(e) => setShiftType(e.target.value)} data-testid="shift-quick-add-type" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
                {SHIFT_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 pb-2">
              <input
                type="checkbox"
                checked={overnight}
                onChange={(e) => setOvernight(e.target.checked)}
                data-testid="shift-quick-add-overnight"
              />
              Overnight (rolls to next day)
            </label>
          </div>

          {/* Conflicts */}
          {conflicts.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-2" data-testid="shift-quick-add-conflicts">
              <p className="text-sm font-semibold text-amber-900 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Conflicts</p>
              <ul className="text-xs text-amber-900 space-y-1">
                {conflicts.map((c, i) => (
                  <li key={i}>· <strong className="uppercase text-[10px] tracking-wider">{c.severity}</strong> {c.message}</li>
                ))}
              </ul>
              {blocking.length > 0 && (
                <div className="pt-2 border-t border-amber-200 space-y-1">
                  <label className="flex items-start gap-2 text-xs text-amber-900">
                    <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="shift-quick-add-override" />
                    Override and proceed (audit-logged)
                  </label>
                  {override && (
                    <input required placeholder="Reason for override" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} className="w-full px-2 py-1 border border-amber-300 rounded text-xs bg-white" data-testid="shift-quick-add-override-reason" />
                  )}
                </div>
              )}
            </div>
          )}

          {error && <p className="text-sm text-red-700" data-testid="shift-quick-add-error">{error}</p>}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex justify-between gap-2 bg-slate-50">
          <div>
            {isEdit && (
              <button
                type="button"
                onClick={remove}
                disabled={deleteBusy || busy}
                data-testid="shift-quick-add-delete"
                className="px-3 py-2 text-sm text-red-700 hover:bg-red-50 rounded-md font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                {deleteBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Delete shift
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-md hover:bg-white">Cancel</button>
            <button type="submit" disabled={busy || deleteBusy} data-testid="shift-quick-add-save" className="px-4 py-2 text-sm bg-[#0D7377] hover:bg-[#0A5C5F] text-white rounded-md font-medium inline-flex items-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {isEdit ? "Save changes" : "Save shift"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
